package com.milta.core.localization

/**
 * Strings for the route / delivery feature (MIL-28 — manual delivery entry).
 *
 * Split into two parts ([RouteStringsCore] + [RouteStringsExtra]) via interface delegation rather
 * than one flat data class: a single ~254-field data class's synthesized `copy$default` needs
 * `1 (this) + N (params) + ceil(N/32) (bitmask ints) + 1 (marker)` JVM method-argument slots, which
 * exceeded the JVM class-file's 255-slot limit at N=254 and threw `ClassFormatError` on any
 * JVM/Android code path touching localization strings (see bug:routestrings-classformaterror-jvm-test).
 * Each part now stays well under that ceiling, with headroom for new fields — `strings.route.xxx`
 * call sites are unaffected, `by` delegation forwards every property unchanged.
 */
interface RouteStringsCore {
    val manualDeliveryTitle: String
    val manualDeliverySubtitle: String
    val recipientNameLabel: String
    val addressLabel: String
    val phoneNumberLabel: String
    val deliveryTimeLimitLabel: String
    val optionalSuffix: String
    val submitButton: String
    /** Label for [submitButton]'s slot when the form is pre-filled from an existing draft being
     * corrected (per-row ✏️ on the recap screen) rather than building a brand new entry — makes
     * explicit that this action commits the correction back into the list, distinct from the
     * sheet's separate final "Enregistrer"/Done action that persists the whole list. */
    val saveEditedDeliveryButton: String
    val deliveryCreated: String
    val errorRecipientNameRequired: String
    val errorRecipientNameTooLong: String
    val errorAddressRequired: String
    val errorAddressTooLong: String
    val errorPhoneTooLong: String
    /** Duplicate draft (same recipientName + address, trimmed/case-insensitive) already in the list. */
    val errorDuplicateDelivery: String
    val errorRouteNotFound: String
    val errorForbidden: String
    val errorValidation: String
    val errorGeneric: String
    /** 409 `error.route_not_in_progress` — POST .../reoptimize on a route that is not InProgress (MIL-35). */
    val errorRouteNotInProgress: String
    /** Fallback label shown when [recipientName] is blank (MIL-28 — recipientName is optional). */
    val unknownRecipientLabel: String
    // ── Driver home (07) + route in progress (08) — MIL-16 ──
    val driverHomeTitle: String
    val driverModeBadge: String
    val routeStatusScheduled: String
    val routeStatusInProgress: String
    val routeStatusCompleted: String
    // Carte de tournée répartie entre plusieurs livreurs (statut agrégé) : « Partielle » = au moins un
    // livreur parti mais pas tous ; sous-titre « 2/5 partis » / « 5/5 terminés » (singulier pour 1 livreur).
    val routeStatusPartial: String
    val driversDepartedOne: String
    val driversDepartedMany: String
    val driversFinishedOne: String
    val driversFinishedMany: String
    // Détail par livreur sur la carte de tournée : livrés / en cours / à faire / échecs / non traités.
    val stopsDeliveredLabel: String
    val stopsInProgressLabel: String
    val stopsTodoLabel: String
    val stopsFailedLabel: String
    val stopsUntreatedLabel: String
    val stopsCountLabel: String
    val startRouteButton: String
    val noRouteTitle: String
    val noRouteSubtitle: String
    /** Past-day variant of [noRouteSubtitle] — period-neutral wording (no "for now"/"currently"),
     * since a closed calendar day can't have a route "still coming". Mirrors emptyTeamRoutesSubtitle. */
    val noRoutePastSubtitle: String
    val routeInProgressTitle: String
    /** Label shown on the route action button when RouteStatus.Completed (MIL-33 — dedicated Completed branch). */
    val routeCompletedTitle: String
    val addStopButton: String
    val errorConflict: String
    // ── Accueil Livreur (07) ──
    val greetingPrefix: String
    val routeOfTheDayLabel: String
    val noOtherRouteLabel: String
    val routeLabel: String
    // ── Import menu (MIL-28) ──
    val importMenuTitle: String
    val importMenuSubtitle: String
    val importModeScanDocumentTitle: String
    val importModeScanDocumentDesc: String
    val importModePdfTitle: String
    val importModePdfDesc: String
    val importModeExcelTitle: String
    val importModeExcelDesc: String
    val importModeManualTitle: String
    val importModeManualDesc: String
    // ── Photo source picker (ScanDocument — camera, gallery or system files choice) ──
    val photoSourceSheetTitle: String
    val photoSourceTakePhoto: String
    val photoSourceChooseGallery: String
    val photoSourceChooseFiles: String
    // ── Create route (MIL-37 base + DOM-02 : Livraisons/Optimiser — Écran 16) ──
    val createRouteTitle: String
    /** Web dashboard: singular "Detail" label shown with the arrow on an openable route row. */
    val routeCardDetailLabel: String
    /** Single import review screen (retour 28/10, point 8 — addition to an existing route): title, per-row "no GPS" warning and per-row retry action. */
    val importReviewTitle: String
    val importReviewMissingGpsWarning: String
    val importReviewRetryGeocodeAction: String
    val createRouteEmptyDrivers: String
    val editDeliveriesAction: String
    val moreDeliveriesLabel: String
    val addDeliveriesAction: String
    /** Manual-entry sheet: confirmation shown after "Ajouter à la liste", followed by "<name>, <address>". */
    val manualEntryAddedConfirmPrefix: String
    /** Same, when the entry was merged into an existing delivery (same recipient + address), followed by "(<name>)". */
    val manualEntryMergedConfirmPrefix: String
    /** Counter label, rendered "$label : $n" (no plural form needed, valid in every language). */
    val manualEntryAddedCountLabel: String
    /** Final button once at least one delivery was added, rendered "$text ($n)". */
    val manualEntryFinishButton: String
    val optimizeRouteButton: String
    /** RoutaR kill switch — same slot as [optimizeRouteButton] when routing optimization is
     * unavailable server-side: creates the route with deliveries in their current order. */
    val createRouteButtonNoOptimize: String
    val errorNoEnterprise: String
    val errorDriverNotFound: String
    val errorNoDeliveries: String
    val errorOptimizationUnavailable: String
    // MIL — reoptimize-in-place (screen 08): every pending stop's address was unresolvable (data
    // problem), distinct from errorOptimizationUnavailable (real RoutaR/geocoding service outage).
    val errorGeocodingFailed: String
    // ── Optimisation / Dispatch (DOM-02 — Écran 17) ──
    val dispatchTitle: String
    val optimizedSuccessTitle: String
    val optimizedSuccessSubtitleSuffix: String
    val stopsStatLabel: String
    // ── Détail de l'arrêt (écran 09 — MIL-34) ──
    /** "Arrêt" — utilisé comme "$stopDetailTitlePrefix $position / $totalStops". */
    val stopDetailTitlePrefix: String
    val stopStatusCurrentLabel: String
    val stopStatusUpcomingLabel: String
    val stopStatusDeliveredLabel: String
    val stopStatusFailedLabel: String
    val stopRecipientLabel: String
    val callActionLabel: String
    val noPhoneLabel: String
    val packagesCountLabel: String
    val navigateButton: String
    /** A7 (MIL-84) — shown under the disabled Navigate button when the stop's address is not HERE-verified. */
    val navigationUnavailableUnverifiedAddress: String
    val deadlineBeforePrefix: String
    val deadlinePastSuffix: String
    /** MIL-41 — entry point to the delivery proof screen, shown only while the stop is still Pending. */
    val validateStopAction: String
    // ── Team dashboard (Gestionnaire) — MIL-38, Écrans 1/2 ──
    val teamDashboardTitle: String
    val todayDateLabel: String
    val statRoutesLabel: String
    val statDeliveriesLabel: String
    val statCompletedLabel: String
    val teamSectionTitle: String
    val routeStartedAtPrefix: String
    val routeCompletedAtPrefix: String
    val routeNotStartedLabel: String
    val emptyTeamRoutesTitle: String
    val emptyTeamRoutesSubtitle: String
    val errorInvalidDate: String
    // ── Route detail (Enterprise view) — MIL-38, Écran 3 ──
    val routeDetailTitle: String
    val driverEyebrowLabel: String
    val failedDeliveryTag: String
    // ── Vérification du chargement (écran 07b — MIL-55) ──
    val driverLoadingCheckTitle: String
    val loadingCheckInstructionText: String
    val expectedPackagesLabel: String
    /** "$confirmLoadCheckboxPrefix $totalPackageCount $confirmLoadCheckboxSuffix". */
    val confirmLoadCheckboxPrefix: String
    val confirmLoadCheckboxSuffix: String
    val reportDiscrepancyToggleLabel: String
    val discrepancyNotePlaceholder: String
    /** Inline field error AND validation-error snackbar (400 error.validation.loading_discrepancy_note). */
    val discrepancyNoteTooLong: String
    val undeliveredStopsSectionTitle: String
    /** "$removedStopNoticePrefix$recipientName$removedStopNoticeMiddle ($remaining $remainingStopsSuffix)". */
    val removedStopNoticePrefix: String
    val removedStopNoticeMiddle: String
    val remainingStopsSuffix: String
    val confirmAndStartButton: String
    // ── Vérification du chargement, refonte "choix unique + slide" (validée sur maquette) ──
    /** Eyebrow of the enterprise row — reads correctly whether the route was assigned by an
     * enterprise or self-created (route.EnterpriseName is the driver's own tenant then). */
    val loadingOnBehalfOfLabel: String
    val loadingExpectedLabel: String
    val loadingQuestionTitle: String
    /** "$loadingAllLoadedPrefix $totalPackageCount $loadingAllLoadedSuffix". */
    val loadingAllLoadedPrefix: String
    val loadingAllLoadedSuffix: String
    val loadingIncompleteOption: String
    val loadingIncompleteHint: String
    val loadingIncompleteRemoveLabel: String
    val slideToStartLabel: String
    // ── "Terminer la tournée" (écran 08, diff — MIL-36) ──
    val completeRouteButton: String
    val completeRouteDialogTitle: String
    /** Suffix appended after the pending-stops count, e.g. "3 $completeRouteDialogPendingCountSuffix (...)." */
    val completeRouteDialogPendingCountSuffix: String
    val completeRouteDialogPendingNote: String
    val completeRouteDialogBody: String
    val completeRouteDialogBodyAllTreated: String
    val completeRouteDialogConfirm: String
    // MIL-81 — mandatory motif collected in the same popup, required whenever Pending stops remain.
    val completeRouteDialogReasonLabel: String
    val completeRouteDialogReasonPlaceholder: String
    val completeRouteDialogNoReasonWarning: String
    val routeCompletedMessage: String
    /** Chip shown on a stop left [com.milta.feature.route.domain.entity.StopStatus.Untreated] after completion. */
    val stopStatusUntreatedLabel: String
    /** 409 error.conflict.route_status — route isn't InProgress anymore. */
    val errorCompleteRouteStatusConflict: String
    /** 409 error.conflict.route_pending_stops — rare safety net, the dialog already covers this case. */
    val errorCompleteRoutePendingStopsConflict: String
    // ── Stop search (écran 08 — Tournée en cours) ──
    /** Magnifier in the NavBar (content description). */
    val stopSearchOpenAction: String
    /** Cross left of the field — closes the search (content description). */
    val stopSearchCloseAction: String
    val stopSearchPlaceholder: String
    /** Cross right of the field — clears the text only (content description). */
    val stopSearchClearTextAction: String
    /** "%1" stops found out of "%2" (2+ or 0 found); [stopSearchResultCountOne] when exactly 1. */
    val stopSearchResultCount: String
    /** "%1" = total stops. */
    val stopSearchResultCountOne: String
    val stopSearchEmptyTitle: String
    val stopSearchEmptyHint: String
    /** Button of the empty state — clears the text, the bar stays open. */
    val stopSearchClearAction: String
    /** "%1" = stop position in the route, shown when the badge doesn't carry the number. */
    val stopPositionLabel: String
}

interface RouteStringsExtra {
    // ── Nouvelle livraison (diff) + Nouvel itinéraire (écran nouveau — MIL-35) ──
    /** Form field placeholder, "Nombre de colis" — distinct from [RouteStringsCore.packagesCountLabel] (unit noun used in meta lines). */
    val packageCountFieldLabel: String
    val reoptimizeSuccessTitle: String
    val backToRouteButton: String
    // ── Dispatch groupé multi-chauffeurs (MIL-39) ──
    /** Link shown on [com.milta.feature.route.presentation.CreateRouteScreen] to switch to the batch flow. */
    val routeBatchMultiDriverLink: String
    val routeBatchSelectTitle: String
    val routeBatchDeliveriesReadyLabel: String
    val routeBatchDriversSectionTitle: String
    /** "$n $routeBatchDriversSelectedSuffix". */
    val routeBatchDriversSelectedSuffix: String
    val routeBatchOptimizeToggleTitle: String
    val routeBatchOptimizeToggleHelp: String
    /** "$routeBatchSubmitButtonPrefix $n $routeBatchDriversWord". */
    val routeBatchSubmitButtonPrefix: String
    val routeBatchDriversWord: String
    /** 503 error.optimization_unavailable on POST route-batches. */
    val routeBatchOptimizationUnavailable: String
    val routeBatchPreviewTitle: String
    /** "$total $routeBatchPreviewEyebrowMiddle $driversCount $routeBatchPreviewEyebrowSuffix". */
    val routeBatchPreviewEyebrowMiddle: String
    val routeBatchPreviewEyebrowSuffix: String
    /** "$n $routeBatchStopsChipSuffix". */
    val routeBatchStopsChipSuffix: String
    val routeBatchDistanceLabel: String
    val routeBatchDurationLabel: String
    val routeBatchDistanceNotCalculated: String
    val routeBatchOptimizeDisabledBanner: String
    val routeBatchReoptimizeButton: String
    val routeBatchDispatchButton: String
    /** 409 error.route_batch_already_dispatched on reoptimize/dispatch. */
    val errorRouteBatchAlreadyDispatched: String
    val routeBatchDispatchedTitle: String
    /** "$n $routeBatchDispatchedSubtitleSuffix". */
    val routeBatchDispatchedSubtitleSuffix: String
    val routeBatchStatRoutesLabel: String
    val routeBatchStatDeliveriesLabel: String
    val routeBatchStatusLabel: String
    /** Localized value shown next to [routeBatchStatusLabel] — batch is always Dispatched at this point (backend enum is machine-only, never rendered raw). */
    val routeBatchStatusDispatchedValue: String
    val routeBatchSentAtLabel: String
    val routeBatchPerDriverTitle: String
    val routeBatchDriverScheduledStatus: String
    val routeBatchBackToHomeButton: String
    /** 400 error.validation.driver_account_ids_too_many — distinct from an empty selection. */
    val errorTooManyDrivers: String
    /** 400 error.validation.deliveries_too_many — distinct from an empty delivery list. */
    val errorTooManyDeliveries: String
    /** FAB on the Gestionnaire dashboard (MIL-38 mockup, Écran 12) — navigates to [CreateRouteScreen]. */
    val createRouteFabLabel: String
    // ── "Créer / Optimiser ma tournée" (MIL-33 — driver self-service, no team picker) ──
    /** Button on [com.milta.feature.route.presentation.DriverHomeScreen], visible with or without an already-assigned route. */
    val createOwnRouteButtonLabel: String
    val createOwnRouteTitle: String
    val createOwnRouteDeliveriesCountSuffix: String
    /** MIL-38 gap fix — badge on the active stop in the Gestionnaire's route detail screen. */
    val stopStatusEnRouteLabel: String
    /** MIL-38 gap fix — CdC §11.3 "today + veille" toggle, second chip label. */
    val yesterdayDateLabel: String
    // ── Tournées multi-jours (Accueil Chauffeur) — segments Passé/Aujourd'hui/Demain ──
    val periodPastLabel: String
    val periodTomorrowLabel: String
    /** Bouton "Voir la tournée" — tournée Scheduled, affiché à côté de [RouteStringsCore.startRouteButton]. */
    val viewRouteButton: String
    /** Bouton "Voir" seul — tournée InProgress ou Completed. */
    val viewButton: String
    val noRouteTomorrowTitle: String
    val noRouteTomorrowSubtitle: String
    val planRouteButtonLabel: String
    // ── Tournées multi-jours v5 (refonte visuelle — en-tête, stat tiles, sections) ──
    val myRoutesTodayTitle: String
    val myRoutesTitle: String
    /** Abréviations des jours, ordre Lun..Dim (index 0 = lundi) — sous-liste "Passé". */
    val weekdayShortNames: List<String>
    /** Noms complets des jours, ordre Lun..Dim (index 0 = lundi) — en-tête "Lundi 8 juin 2026". */
    val weekdayFullNames: List<String>
    /** Noms complets des mois, ordre Jan..Déc (index 0 = janvier) — en-tête "Lundi 8 juin 2026". */
    val monthNames: List<String>
    val statRoutesTodayLabel: String
    val statStopsTodayLabel: String
    val statOnTimeLabel: String
    val statAvgProgressLabel: String
    val inProgressSectionTitle: String
    val upcomingSectionTitle: String
    val doneSectionTitle: String
    val seeAllLabel: String
    val continueRouteButton: String
    val detailsLinkLabel: String
    // ── Preuve de livraison (écran "Valider la livraison" — MIL-41) ──
    val deliveryProofTitle: String
    val deliveryProofStatusSectionLabel: String
    val deliveryProofPhotoSectionLabel: String
    val deliveryProofPhotoHint: String
    val deliveryProofTakePhotoCta: String
    val deliveryProofRetakePhotoCta: String
    val deliveryProofRetakeChipLabel: String
    val deliveryProofCommentSectionLabel: String
    /** Generic label of a stop failed because the driver closed the route with it still Pending (A2). */
    val failureReasonRouteClosedByDriver: String
    /** Appended to [deliveryProofCommentSectionLabel] when [DeliveryOutcome.Failed] is selected. */
    val deliveryProofCommentRequiredSuffix: String
    val deliveryProofCommentPlaceholderOptional: String
    val deliveryProofCommentPlaceholderRequired: String
    val deliveryProofCommentRequiredError: String
    val deliveryProofSubmitButton: String
    /** 400 error.invalid_photo. */
    val deliveryProofErrorInvalidPhoto: String
    /** 409 error.conflict.delivery_status (AC5) — whole-form replacement. */
    val deliveryProofAlreadyProcessedBanner: String
    // ── Zoom + téléchargement de la photo de preuve (écran 09 "Détail de l'arrêt" — MIL-83) ──
    val proofPhotoViewerDownloadLabel: String
    val proofPhotoViewerCloseLabel: String
    val proofPhotoDownloadedMessage: String
    val proofPhotoDownloadErrorMessage: String
    // ── Confirmation (écran "Livraison validée" — MIL-41) ──
    val deliveryConfirmTitle: String
    val deliveryConfirmSuccessTitle: String
    val deliveryConfirmSuccessSubtitle: String
    val deliveryConfirmFailedTitle: String
    val deliveryConfirmFailedSubtitle: String
    val deliveryConfirmRouteCompletedTitle: String
    val deliveryConfirmRouteCompletedSubtitle: String
    val deliveryConfirmValidatedAtLabel: String
    /** "$deliveryConfirmNextStopEyebrowPrefix · $position / $total". */
    val deliveryConfirmNextStopEyebrowPrefix: String
    val deliveryConfirmGoToNextStopButton: String
    val deliveryConfirmAllDoneMessage: String
    // ── Synchronisation hors ligne (MIL-43 — file d'attente de validations hors connexion) ──
    val offlineBannerTitle: String
    val offlineBannerRouteSubtitle: String
    val offlineBannerValidateSubtitle: String
    val offlineSyncingBannerTitle: String
    /** "$synced/$total $offlineSyncingSubtitleSuffix". */
    val offlineSyncingSubtitleSuffix: String
    val offlineRetryBannerTitle: String
    val offlineRetryBannerSubtitle: String
    /** "$count $offlineSyncSuccessToastSuffix". */
    val offlineSyncSuccessToastSuffix: String
    val offlineQueuedBadgePendingLabel: String
    val offlineQueuedBadgeSyncingLabel: String
    val offlineQueuedBadgeFailedLabel: String
    val offlineQueuedBadgeConflictLabel: String
    val offlineDismissConflictAction: String
    /** "$offlineValidatedLocallyAtPrefix $time" — appended after "· " in the stop row's meta line. */
    val offlineValidatedLocallyAtPrefix: String
    val offlineAddStopRequiresConnectionSuffix: String
    val deliveryProofOfflineSubmitButton: String
    val deliveryProofSavedLocallyTitle: String
    val deliveryProofSavedLocallySubtitle: String
    // ── Tournée Scheduled ouverte en lecture seule (bug fix — "Voir" depuis l'Accueil ne doit pas
    // laisser valider un arrêt avant que la tournée soit réellement démarrée) ──
    val routeScheduledBannerTitle: String
    val routeScheduledBannerSubtitle: String
    // ── Géocodage (MIL-45) — Détail tournée Gestionnaire ──
    /** Warning tag under the address of a stop still lacking GPS coordinates. */
    val geocodeIssueTag: String
    /** Pill/action shown when [com.milta.feature.route.domain.entity.Route.hasGeocodeIssues]. */
    val regeocodeAction: String
    val regeocodeSuccessMessage: String
    /** Titre affiché à la place de "Bonjour, {Prénom}" dans le header partagé Home mobile, mode
     * Entreprise (voir [com.milta.feature.route.presentation.HomeHeaderBar]). Terme technique
     * courant, identique dans les 9 langues. */
    val dashboardTitle: String
    /** "Tout voir" link next to [RouteStringsCore.teamSectionTitle], navigates to the Équipe tab (Écran 12 mockup gap fix). */
    val viewAllAction: String
    // ── Suppression d'une tournée (Accueil Livreur, écran 07 — MIL-65) ──
    val deleteRouteDialogTitle: String
    val deleteRouteDialogMessage: String
    /** 409 `error.conflict.route_already_started` on DELETE, ET même texte pour le tap sur le
     * bouton "Supprimer" grisé d'une tournée InProgress (aucun appel API dans ce dernier cas). */
    val errorRouteDeleteConflict: String
    /** 409 `error.conflict.driver_has_active_route` sur PATCH .../start — une AUTRE tournée du
     * même chauffeur est déjà InProgress (MIL-65). */
    val errorDriverHasActiveRoute: String
    // ── Chauffeur sans arrêt dans un lot (bug fix — RoutaR peut laisser un [RouteBatchDriverProposal]
    // à deliveryCount == 0 ; MUST warn before letting the manager dispatch a driver with no stops) ──
    /** Warning tag shown on [com.milta.feature.route.presentation.RouteBatchPreviewScreen]'s driver
     * card when [com.milta.feature.route.domain.entity.RouteBatchDriverProposal.deliveryCount] == 0. */
    val routeBatchNoStopsTag: String
    val routeBatchNoStopsDialogTitle: String
    val routeBatchNoStopsDialogMessage: String
    /** "$n $routeBatchAddressesToReviewTag" warning row on [com.milta.feature.route.presentation.RouteBatchPreviewScreen]'s
     * driver card, shown when at least one of that driver's deliveries has
     * [com.milta.feature.route.domain.entity.GeocodeStatus] != Geocoded (HERE validation failed or
     * degraded) — never implies the address text itself was changed, just flags it for review. */
    val routeBatchAddressesToReviewTag: String
    // ── Retour client 17/09/2026, point 10 — édition d'un arrêt importé (DeliveryEntrySheet) ──
    /** En mode édition, tête de l'encart listant les autres livraisons du même import, sous le
     * formulaire — composé "$otherDeliveriesInImportLabel ($count)". */
    val otherDeliveriesInImportLabel: String
    // ── Retour client 17/09/2026, point 12 — consultation de la preuve d'un arrêt terminé ──
    /** [com.milta.feature.route.presentation.StopDetailScreen] / GestionnaireRouteDetailScreen —
     * shown in place of the photo when it's absent (never taken) or failed to load. */
    val proofPhotoUnavailableLabel: String
    // ── Retour Mike 27/10, point 3 — photo facultative sur les tournées créées par le chauffeur ──
    /** [com.milta.feature.route.presentation.DeliveryProofScreen] — photo block title when the
     * route doesn't require a proof photo ("Photo (facultative)"). */
    val deliveryProofPhotoOptionalSectionLabel: String
    /** Shown in place of the photo of a proof validated without one ("Sans photo") — never
     * zoomable nor downloadable. */
    val proofWithoutPhotoLabel: String
    /** Retour 28/10 (point 1) — confirmation before the loading check's ✕ removes a stop. */
    val removeStopDialogTitle: String
    /** {name} = recipient (or address), {count} = parcels. */
    val removeStopDialogMessage: String
    /** Retour 28/10 (point 1) — snackbar after the creation list's ✕, with [undoAction]. */
    val deliveryRemovedMessage: String
    /** Snackbar action that puts the removed delivery back at its place. */
    val undoAction: String
    /** Retour 28/10 (point 5) — the phone can't start the call (no dialer): retrying won't help. */
    val callLaunchFailed: String
    /** Retour 28/10 (point 3) — manual entry sheet closed with 1 delivery not yet in the list. */
    val keepDraftsDialogTitleOne: String
    /** Same, {count} deliveries. */
    val keepDraftsDialogTitleMany: String
    /** Body of the keep dialog. */
    val keepDraftsDialogMessage: String
    /** Keeps them, exactly like "Terminer (N)". */
    val keepDraftsAction: String
    /** Retour 28/10 (point 6) — green snackbar after adding deliveries to an existing route. */
    val deliveriesAddedToRouteOne: String
    /** Same, {count} deliveries. */
    val deliveriesAddedToRouteMany: String
    /** Retour 28/10 (point 6) — green snackbar after linking a driver by code; {name} = first + last name. */
    val driverJoinedTeamMessage: String
    /** Retour 28/10 (point 6) — driver route created without optimization and no reason from the server. */
    val ownRouteCreatedNotOptimizedMessage: String
    /** Retour 28/10 (brouillon C) — sheet when leaving the creation list with deliveries in it. */
    val draftExitTitle: String
    /** {packages} = parcels of the single delivery. */
    val draftExitMessageOne: String
    /** {count} deliveries, {packages} parcels. */
    val draftExitMessageMany: String
    /** Leaves, the draft is kept. */
    val draftKeepAction: String
    /** Leaves and empties the draft (explicit choice, no second confirmation). */
    val draftDeleteDeliveriesAction: String
    /** Closes the sheet, stays on the list. */
    val draftContinueAction: String
    /** Snackbar after "Garder pour plus tard". */
    val draftSavedMessage: String
    /** Draft card on the Enterprise dashboard / driver home. */
    val draftCardTitle: String
    /** {date} = dd/MM, appended to the card's "N livraisons · X colis". */
    val draftForDate: String
    /** Reopens the list with everything kept. */
    val draftResumeAction: String
    /** Confirmation of the card's "Supprimer". */
    val draftDeleteDialogTitle: String
    /** {packages} = parcels of the single delivery. */
    val draftDeleteDialogMessageOne: String
    /** {count} deliveries, {packages} parcels. */
    val draftDeleteDialogMessageMany: String
    /** Import menu card when a draft exists. */
    val draftResumeImportTitle: String
    /** Above the 4 import cards when a draft exists. */
    val draftImportAddsHint: String

    // ── Réunion 28/10 (point 5) — finished stop, Enterprise view (list + detail) ──
    /** Photo banner: "Livré à" + " HH:mm". */
    val stopDeliveredAtPrefix: String
    /** Photo banner: "Échec à" + " HH:mm". */
    val stopFailedAtPrefix: String
    /** Validated before (or at) the deadline. */
    val stopOnTimeLabel: String
    /** "En retard de" + " 12 min" / " 1 h 05". */
    val stopLateByPrefix: String
    /** Detail row label: delays since the start / the previous stop. */
    val stopTimeLabel: String
    /** "1 h 05" + " après le départ". */
    val stopAfterStartSuffix: String
    /** "12 min" + " après l'arrêt précédent". */
    val stopAfterPreviousSuffix: String
    /** Detail row label above the motif / comment. */
    val stopDriverCommentLabel: String
    /** Detail row label above the package count. */
    val stopPackagesLabel: String
    /** Hours unit of a duration, "1 h 05". */
    val durationHoursUnit: String
    /** Minutes unit of a duration, "12 min". */
    val durationMinutesUnit: String
}

class RouteStrings(core: RouteStringsCore, extra: RouteStringsExtra) :
    RouteStringsCore by core,
    RouteStringsExtra by extra

data class RouteStringsCoreImpl(
    override val manualDeliveryTitle: String,
    override val manualDeliverySubtitle: String,
    override val recipientNameLabel: String,
    override val addressLabel: String,
    override val phoneNumberLabel: String,
    override val deliveryTimeLimitLabel: String,
    override val optionalSuffix: String,
    override val submitButton: String,
    override val saveEditedDeliveryButton: String,
    override val deliveryCreated: String,
    override val errorRecipientNameRequired: String,
    override val errorRecipientNameTooLong: String,
    override val errorAddressRequired: String,
    override val errorAddressTooLong: String,
    override val errorPhoneTooLong: String,
    /** Duplicate draft (same recipientName + address, trimmed/case-insensitive) already in the list. */
    override val errorDuplicateDelivery: String,
    override val errorRouteNotFound: String,
    override val errorForbidden: String,
    override val errorValidation: String,
    override val errorGeneric: String,
    /** 409 `error.route_not_in_progress` — POST .../reoptimize on a route that is not InProgress (MIL-35). */
    override val errorRouteNotInProgress: String,
    /** Fallback label shown when [recipientName] is blank (MIL-28 — recipientName is optional). */
    override val unknownRecipientLabel: String,
    // ── Driver home (07) + route in progress (08) — MIL-16 ──
    override val driverHomeTitle: String,
    override val driverModeBadge: String,
    override val routeStatusScheduled: String,
    override val routeStatusInProgress: String,
    override val routeStatusCompleted: String,
    override val routeStatusPartial: String,
    override val driversDepartedOne: String,
    override val driversDepartedMany: String,
    override val driversFinishedOne: String,
    override val driversFinishedMany: String,
    override val stopsDeliveredLabel: String,
    override val stopsInProgressLabel: String,
    override val stopsTodoLabel: String,
    override val stopsFailedLabel: String,
    override val stopsUntreatedLabel: String,
    override val stopsCountLabel: String,
    override val startRouteButton: String,
    override val noRouteTitle: String,
    override val noRouteSubtitle: String,
    /** Past-day variant of [noRouteSubtitle] — period-neutral wording (no "for now"/"currently"),
     * since a closed calendar day can't have a route "still coming". Mirrors emptyTeamRoutesSubtitle. */
    override val noRoutePastSubtitle: String,
    override val routeInProgressTitle: String,
    /** Label shown on the route action button when RouteStatus.Completed (MIL-33 — dedicated Completed branch). */
    override val routeCompletedTitle: String,
    override val addStopButton: String,
    override val errorConflict: String,
    // ── Accueil Livreur (07) ──
    override val greetingPrefix: String,
    override val routeOfTheDayLabel: String,
    override val noOtherRouteLabel: String,
    override val routeLabel: String,
    // ── Import menu (MIL-28) ──
    override val importMenuTitle: String,
    override val importMenuSubtitle: String,
    override val importModeScanDocumentTitle: String,
    override val importModeScanDocumentDesc: String,
    override val importModePdfTitle: String,
    override val importModePdfDesc: String,
    override val importModeExcelTitle: String,
    override val importModeExcelDesc: String,
    override val importModeManualTitle: String,
    override val importModeManualDesc: String,
    // ── Photo source picker (ScanDocument — camera, gallery or system files choice) ──
    override val photoSourceSheetTitle: String,
    override val photoSourceTakePhoto: String,
    override val photoSourceChooseGallery: String,
    override val photoSourceChooseFiles: String,
    // ── Create route (MIL-37 base + DOM-02 : Livraisons/Optimiser — Écran 16) ──
    override val createRouteTitle: String,
    override val routeCardDetailLabel: String,
    override val importReviewTitle: String,
    override val importReviewMissingGpsWarning: String,
    override val importReviewRetryGeocodeAction: String,
    override val createRouteEmptyDrivers: String,
    override val editDeliveriesAction: String,
    override val moreDeliveriesLabel: String,
    override val addDeliveriesAction: String,
    override val manualEntryAddedConfirmPrefix: String,
    override val manualEntryMergedConfirmPrefix: String,
    override val manualEntryAddedCountLabel: String,
    override val manualEntryFinishButton: String,
    override val optimizeRouteButton: String,
    /** RoutaR kill switch — same slot as [optimizeRouteButton] when routing optimization is
     * unavailable server-side: creates the route with deliveries in their current order. */
    override val createRouteButtonNoOptimize: String,
    override val errorNoEnterprise: String,
    override val errorDriverNotFound: String,
    override val errorNoDeliveries: String,
    override val errorOptimizationUnavailable: String,
    // MIL — reoptimize-in-place (screen 08): every pending stop's address was unresolvable (data
    // problem), distinct from errorOptimizationUnavailable (real RoutaR/geocoding service outage).
    override val errorGeocodingFailed: String,
    // ── Optimisation / Dispatch (DOM-02 — Écran 17) ──
    override val dispatchTitle: String,
    override val optimizedSuccessTitle: String,
    override val optimizedSuccessSubtitleSuffix: String,
    override val stopsStatLabel: String,
    // ── Détail de l'arrêt (écran 09 — MIL-34) ──
    /** "Arrêt" — utilisé comme "$stopDetailTitlePrefix $position / $totalStops". */
    override val stopDetailTitlePrefix: String,
    override val stopStatusCurrentLabel: String,
    override val stopStatusUpcomingLabel: String,
    override val stopStatusDeliveredLabel: String,
    override val stopStatusFailedLabel: String,
    override val stopRecipientLabel: String,
    override val callActionLabel: String,
    override val noPhoneLabel: String,
    override val packagesCountLabel: String,
    override val navigateButton: String,
    override val navigationUnavailableUnverifiedAddress: String,
    override val deadlineBeforePrefix: String,
    override val deadlinePastSuffix: String,
    /** MIL-41 — entry point to the delivery proof screen, shown only while the stop is still Pending. */
    override val validateStopAction: String,
    // ── Team dashboard (Gestionnaire) — MIL-38, Écrans 1/2 ──
    override val teamDashboardTitle: String,
    override val todayDateLabel: String,
    override val statRoutesLabel: String,
    override val statDeliveriesLabel: String,
    override val statCompletedLabel: String,
    override val teamSectionTitle: String,
    override val routeStartedAtPrefix: String,
    override val routeCompletedAtPrefix: String,
    override val routeNotStartedLabel: String,
    override val emptyTeamRoutesTitle: String,
    override val emptyTeamRoutesSubtitle: String,
    override val errorInvalidDate: String,
    // ── Route detail (Enterprise view) — MIL-38, Écran 3 ──
    override val routeDetailTitle: String,
    override val driverEyebrowLabel: String,
    override val failedDeliveryTag: String,
    // ── Vérification du chargement (écran 07b — MIL-55) ──
    override val driverLoadingCheckTitle: String,
    override val loadingCheckInstructionText: String,
    override val expectedPackagesLabel: String,
    /** "$confirmLoadCheckboxPrefix $totalPackageCount $confirmLoadCheckboxSuffix". */
    override val confirmLoadCheckboxPrefix: String,
    override val confirmLoadCheckboxSuffix: String,
    override val reportDiscrepancyToggleLabel: String,
    override val discrepancyNotePlaceholder: String,
    /** Inline field error AND validation-error snackbar (400 error.validation.loading_discrepancy_note). */
    override val discrepancyNoteTooLong: String,
    override val undeliveredStopsSectionTitle: String,
    /** "$removedStopNoticePrefix$recipientName$removedStopNoticeMiddle ($remaining $remainingStopsSuffix)". */
    override val removedStopNoticePrefix: String,
    override val removedStopNoticeMiddle: String,
    override val remainingStopsSuffix: String,
    override val confirmAndStartButton: String,
    override val loadingOnBehalfOfLabel: String,
    override val loadingExpectedLabel: String,
    override val loadingQuestionTitle: String,
    override val loadingAllLoadedPrefix: String,
    override val loadingAllLoadedSuffix: String,
    override val loadingIncompleteOption: String,
    override val loadingIncompleteHint: String,
    override val loadingIncompleteRemoveLabel: String,
    override val slideToStartLabel: String,
    // ── "Terminer la tournée" (écran 08, diff — MIL-36) ──
    override val completeRouteButton: String,
    override val completeRouteDialogTitle: String,
    /** Suffix appended after the pending-stops count, e.g. "3 $completeRouteDialogPendingCountSuffix (...)." */
    override val completeRouteDialogPendingCountSuffix: String,
    override val completeRouteDialogPendingNote: String,
    override val completeRouteDialogBody: String,
    override val completeRouteDialogBodyAllTreated: String,
    override val completeRouteDialogConfirm: String,
    override val completeRouteDialogReasonLabel: String,
    override val completeRouteDialogReasonPlaceholder: String,
    override val completeRouteDialogNoReasonWarning: String,
    override val routeCompletedMessage: String,
    /** Chip shown on a stop left [com.milta.feature.route.domain.entity.StopStatus.Untreated] after completion. */
    override val stopStatusUntreatedLabel: String,
    /** 409 error.conflict.route_status — route isn't InProgress anymore. */
    override val errorCompleteRouteStatusConflict: String,
    /** 409 error.conflict.route_pending_stops — rare safety net, the dialog already covers this case. */
    override val errorCompleteRoutePendingStopsConflict: String,
    override val stopSearchOpenAction: String,
    override val stopSearchCloseAction: String,
    override val stopSearchPlaceholder: String,
    override val stopSearchClearTextAction: String,
    override val stopSearchResultCount: String,
    override val stopSearchResultCountOne: String,
    override val stopSearchEmptyTitle: String,
    override val stopSearchEmptyHint: String,
    override val stopSearchClearAction: String,
    override val stopPositionLabel: String,
) : RouteStringsCore

data class RouteStringsExtraImpl(
    // ── Nouvelle livraison (diff) + Nouvel itinéraire (écran nouveau — MIL-35) ──
    /** Form field placeholder, "Nombre de colis" — distinct from [RouteStringsCore.packagesCountLabel] (unit noun used in meta lines). */
    override val packageCountFieldLabel: String,
    override val reoptimizeSuccessTitle: String,
    override val backToRouteButton: String,
    // ── Dispatch groupé multi-chauffeurs (MIL-39) ──
    /** Link shown on [com.milta.feature.route.presentation.CreateRouteScreen] to switch to the batch flow. */
    override val routeBatchMultiDriverLink: String,
    override val routeBatchSelectTitle: String,
    override val routeBatchDeliveriesReadyLabel: String,
    override val routeBatchDriversSectionTitle: String,
    /** "$n $routeBatchDriversSelectedSuffix". */
    override val routeBatchDriversSelectedSuffix: String,
    override val routeBatchOptimizeToggleTitle: String,
    override val routeBatchOptimizeToggleHelp: String,
    /** "$routeBatchSubmitButtonPrefix $n $routeBatchDriversWord". */
    override val routeBatchSubmitButtonPrefix: String,
    override val routeBatchDriversWord: String,
    /** 503 error.optimization_unavailable on POST route-batches. */
    override val routeBatchOptimizationUnavailable: String,
    override val routeBatchPreviewTitle: String,
    /** "$total $routeBatchPreviewEyebrowMiddle $driversCount $routeBatchPreviewEyebrowSuffix". */
    override val routeBatchPreviewEyebrowMiddle: String,
    override val routeBatchPreviewEyebrowSuffix: String,
    /** "$n $routeBatchStopsChipSuffix". */
    override val routeBatchStopsChipSuffix: String,
    override val routeBatchDistanceLabel: String,
    override val routeBatchDurationLabel: String,
    override val routeBatchDistanceNotCalculated: String,
    override val routeBatchOptimizeDisabledBanner: String,
    override val routeBatchReoptimizeButton: String,
    override val routeBatchDispatchButton: String,
    /** 409 error.route_batch_already_dispatched on reoptimize/dispatch. */
    override val errorRouteBatchAlreadyDispatched: String,
    override val routeBatchDispatchedTitle: String,
    /** "$n $routeBatchDispatchedSubtitleSuffix". */
    override val routeBatchDispatchedSubtitleSuffix: String,
    override val routeBatchStatRoutesLabel: String,
    override val routeBatchStatDeliveriesLabel: String,
    override val routeBatchStatusLabel: String,
    /** Localized value shown next to [routeBatchStatusLabel] — batch is always Dispatched at this point (backend enum is machine-only, never rendered raw). */
    override val routeBatchStatusDispatchedValue: String,
    override val routeBatchSentAtLabel: String,
    override val routeBatchPerDriverTitle: String,
    override val routeBatchDriverScheduledStatus: String,
    override val routeBatchBackToHomeButton: String,
    /** 400 error.validation.driver_account_ids_too_many — distinct from an empty selection. */
    override val errorTooManyDrivers: String,
    /** 400 error.validation.deliveries_too_many — distinct from an empty delivery list. */
    override val errorTooManyDeliveries: String,
    /** FAB on the Gestionnaire dashboard (MIL-38 mockup, Écran 12) — navigates to [CreateRouteScreen]. */
    override val createRouteFabLabel: String,
    // ── "Créer / Optimiser ma tournée" (MIL-33 — driver self-service, no team picker) ──
    /** Button on [com.milta.feature.route.presentation.DriverHomeScreen], visible with or without an already-assigned route. */
    override val createOwnRouteButtonLabel: String,
    override val createOwnRouteTitle: String,
    override val createOwnRouteDeliveriesCountSuffix: String,
    /** MIL-38 gap fix — badge on the active stop in the Gestionnaire's route detail screen. */
    override val stopStatusEnRouteLabel: String,
    /** MIL-38 gap fix — CdC §11.3 "today + veille" toggle, second chip label. */
    override val yesterdayDateLabel: String,
    // ── Tournées multi-jours (Accueil Chauffeur) — segments Passé/Aujourd'hui/Demain ──
    override val periodPastLabel: String,
    override val periodTomorrowLabel: String,
    /** Bouton "Voir la tournée" — tournée Scheduled, affiché à côté de [RouteStringsCore.startRouteButton]. */
    override val viewRouteButton: String,
    /** Bouton "Voir" seul — tournée InProgress ou Completed. */
    override val viewButton: String,
    override val noRouteTomorrowTitle: String,
    override val noRouteTomorrowSubtitle: String,
    override val planRouteButtonLabel: String,
    // ── Tournées multi-jours v5 (refonte visuelle — en-tête, stat tiles, sections) ──
    override val myRoutesTodayTitle: String,
    override val myRoutesTitle: String,
    /** Abréviations des jours, ordre Lun..Dim (index 0 = lundi) — sous-liste "Passé". */
    override val weekdayShortNames: List<String>,
    /** Noms complets des jours, ordre Lun..Dim (index 0 = lundi) — en-tête "Lundi 8 juin 2026". */
    override val weekdayFullNames: List<String>,
    /** Noms complets des mois, ordre Jan..Déc (index 0 = janvier) — en-tête "Lundi 8 juin 2026". */
    override val monthNames: List<String>,
    override val statRoutesTodayLabel: String,
    override val statStopsTodayLabel: String,
    override val statOnTimeLabel: String,
    override val statAvgProgressLabel: String,
    override val inProgressSectionTitle: String,
    override val upcomingSectionTitle: String,
    override val doneSectionTitle: String,
    override val seeAllLabel: String,
    override val continueRouteButton: String,
    override val detailsLinkLabel: String,
    // ── Preuve de livraison (écran "Valider la livraison" — MIL-41) ──
    override val deliveryProofTitle: String,
    override val deliveryProofStatusSectionLabel: String,
    override val deliveryProofPhotoSectionLabel: String,
    override val deliveryProofPhotoHint: String,
    override val deliveryProofTakePhotoCta: String,
    override val deliveryProofRetakePhotoCta: String,
    override val deliveryProofRetakeChipLabel: String,
    override val deliveryProofCommentSectionLabel: String,
    override val failureReasonRouteClosedByDriver: String,
    /** Appended to [deliveryProofCommentSectionLabel] when [DeliveryOutcome.Failed] is selected. */
    override val deliveryProofCommentRequiredSuffix: String,
    override val deliveryProofCommentPlaceholderOptional: String,
    override val deliveryProofCommentPlaceholderRequired: String,
    override val deliveryProofCommentRequiredError: String,
    override val deliveryProofSubmitButton: String,
    /** 400 error.invalid_photo. */
    override val deliveryProofErrorInvalidPhoto: String,
    /** 409 error.conflict.delivery_status (AC5) — whole-form replacement. */
    override val deliveryProofAlreadyProcessedBanner: String,
    override val proofPhotoViewerDownloadLabel: String,
    override val proofPhotoViewerCloseLabel: String,
    override val proofPhotoDownloadedMessage: String,
    override val proofPhotoDownloadErrorMessage: String,
    // ── Confirmation (écran "Livraison validée" — MIL-41) ──
    override val deliveryConfirmTitle: String,
    override val deliveryConfirmSuccessTitle: String,
    override val deliveryConfirmSuccessSubtitle: String,
    override val deliveryConfirmFailedTitle: String,
    override val deliveryConfirmFailedSubtitle: String,
    override val deliveryConfirmRouteCompletedTitle: String,
    override val deliveryConfirmRouteCompletedSubtitle: String,
    override val deliveryConfirmValidatedAtLabel: String,
    /** "$deliveryConfirmNextStopEyebrowPrefix · $position / $total". */
    override val deliveryConfirmNextStopEyebrowPrefix: String,
    override val deliveryConfirmGoToNextStopButton: String,
    override val deliveryConfirmAllDoneMessage: String,
    // ── Synchronisation hors ligne (MIL-43 — file d'attente de validations hors connexion) ──
    override val offlineBannerTitle: String,
    override val offlineBannerRouteSubtitle: String,
    override val offlineBannerValidateSubtitle: String,
    override val offlineSyncingBannerTitle: String,
    /** "$synced/$total $offlineSyncingSubtitleSuffix". */
    override val offlineSyncingSubtitleSuffix: String,
    override val offlineRetryBannerTitle: String,
    override val offlineRetryBannerSubtitle: String,
    /** "$count $offlineSyncSuccessToastSuffix". */
    override val offlineSyncSuccessToastSuffix: String,
    override val offlineQueuedBadgePendingLabel: String,
    override val offlineQueuedBadgeSyncingLabel: String,
    override val offlineQueuedBadgeFailedLabel: String,
    override val offlineQueuedBadgeConflictLabel: String,
    override val offlineDismissConflictAction: String,
    /** "$offlineValidatedLocallyAtPrefix $time" — appended after "· " in the stop row's meta line. */
    override val offlineValidatedLocallyAtPrefix: String,
    override val offlineAddStopRequiresConnectionSuffix: String,
    override val deliveryProofOfflineSubmitButton: String,
    override val deliveryProofSavedLocallyTitle: String,
    override val deliveryProofSavedLocallySubtitle: String,
    // ── Tournée Scheduled ouverte en lecture seule (bug fix — "Voir" depuis l'Accueil ne doit pas
    // laisser valider un arrêt avant que la tournée soit réellement démarrée) ──
    override val routeScheduledBannerTitle: String,
    override val routeScheduledBannerSubtitle: String,
    // ── Géocodage (MIL-45) — Détail tournée Gestionnaire ──
    /** Warning tag under the address of a stop still lacking GPS coordinates. */
    override val geocodeIssueTag: String,
    /** Pill/action shown when [com.milta.feature.route.domain.entity.Route.hasGeocodeIssues]. */
    override val regeocodeAction: String,
    override val regeocodeSuccessMessage: String,
    /** Titre affiché à la place de "Bonjour, {Prénom}" dans le header partagé Home mobile, mode
     * Entreprise (voir [com.milta.feature.route.presentation.HomeHeaderBar]). Terme technique
     * courant, identique dans les 9 langues. */
    override val dashboardTitle: String,
    /** "Tout voir" link next to [RouteStringsCore.teamSectionTitle], navigates to the Équipe tab (Écran 12 mockup gap fix). */
    override val viewAllAction: String,
    // ── Suppression d'une tournée (Accueil Livreur, écran 07 — MIL-65) ──
    override val deleteRouteDialogTitle: String,
    override val deleteRouteDialogMessage: String,
    /** 409 `error.conflict.route_already_started` on DELETE, ET même texte pour le tap sur le
     * bouton "Supprimer" grisé d'une tournée InProgress (aucun appel API dans ce dernier cas). */
    override val errorRouteDeleteConflict: String,
    /** 409 `error.conflict.driver_has_active_route` sur PATCH .../start — une AUTRE tournée du
     * même chauffeur est déjà InProgress (MIL-65). */
    override val errorDriverHasActiveRoute: String,
    // ── Chauffeur sans arrêt dans un lot (bug fix — RoutaR peut laisser un [RouteBatchDriverProposal]
    // à deliveryCount == 0 ; MUST warn before letting the manager dispatch a driver with no stops) ──
    /** Warning tag shown on [com.milta.feature.route.presentation.RouteBatchPreviewScreen]'s driver
     * card when [com.milta.feature.route.domain.entity.RouteBatchDriverProposal.deliveryCount] == 0. */
    override val routeBatchNoStopsTag: String,
    override val routeBatchNoStopsDialogTitle: String,
    override val routeBatchNoStopsDialogMessage: String,
    override val routeBatchAddressesToReviewTag: String,
    override val otherDeliveriesInImportLabel: String,
    override val proofPhotoUnavailableLabel: String,
    override val deliveryProofPhotoOptionalSectionLabel: String,
    override val proofWithoutPhotoLabel: String,
    override val removeStopDialogTitle: String,
    override val removeStopDialogMessage: String,
    override val deliveryRemovedMessage: String,
    override val undoAction: String,
    override val callLaunchFailed: String,
    override val keepDraftsDialogTitleOne: String,
    override val keepDraftsDialogTitleMany: String,
    override val keepDraftsDialogMessage: String,
    override val keepDraftsAction: String,
    override val deliveriesAddedToRouteOne: String,
    override val deliveriesAddedToRouteMany: String,
    override val driverJoinedTeamMessage: String,
    override val ownRouteCreatedNotOptimizedMessage: String,
    override val draftExitTitle: String,
    override val draftExitMessageOne: String,
    override val draftExitMessageMany: String,
    override val draftKeepAction: String,
    override val draftDeleteDeliveriesAction: String,
    override val draftContinueAction: String,
    override val draftSavedMessage: String,
    override val draftCardTitle: String,
    override val draftForDate: String,
    override val draftResumeAction: String,
    override val draftDeleteDialogTitle: String,
    override val draftDeleteDialogMessageOne: String,
    override val draftDeleteDialogMessageMany: String,
    override val draftResumeImportTitle: String,
    override val draftImportAddsHint: String,
    override val stopDeliveredAtPrefix: String,
    override val stopFailedAtPrefix: String,
    override val stopOnTimeLabel: String,
    override val stopLateByPrefix: String,
    override val stopTimeLabel: String,
    override val stopAfterStartSuffix: String,
    override val stopAfterPreviousSuffix: String,
    override val stopDriverCommentLabel: String,
    override val stopPackagesLabel: String,
    override val durationHoursUnit: String,
    override val durationMinutesUnit: String,
) : RouteStringsExtra

/** "Garder la livraison ?" / "Garder les N livraisons ?" (retour 28/10, point 3). */
fun RouteStrings.keepDraftsDialogTitle(count: Int): String =
    if (count == 1) keepDraftsDialogTitleOne else keepDraftsDialogTitleMany.replace("{count}", count.toString())

/** "1 livraison ajoutée à la tournée" / "N livraisons ajoutées à la tournée" (retour 28/10, point 6). */
fun RouteStrings.deliveriesAddedToRouteText(count: Int): String =
    if (count == 1) deliveriesAddedToRouteOne else deliveriesAddedToRouteMany.replace("{count}", count.toString())

/** "1 arrêt sur N" / "X arrêts sur N" under the stop search field (écran 08). */
fun RouteStrings.stopSearchResultCountText(count: Int, total: Int): String =
    if (count == 1) {
        stopSearchResultCountOne.replace("%1", total.toString())
    } else {
        stopSearchResultCount.replace("%1", count.toString()).replace("%2", total.toString())
    }

/** "n° X" on a stop search result whose badge shows a status icon instead of the number. */
fun RouteStrings.stopPositionText(position: Int): String = stopPositionLabel.replace("%1", position.toString())
